/**
 * Order-book fulfilment arithmetic: what was ordered, what the pharmacy handed
 * over, what the ward accepted — and the user-facing labels derived from them.
 *
 * Import-free beyond ./prescriptionStatus (itself constants-only) on purpose:
 * pharmacyMappers reaches axios ESM, which Jest cannot load. Same escape hatch
 * as prescriptionStatus / dispensingEntity.
 *
 * Quantity semantics (pharamcy.md §1, verified against order_lifecycle.py):
 *   - ordered   = Σ details.total_quantity — NULL/0 means UNKNOWN, never zero.
 *                 9 of 14 rows on admission 66 have no denominator; an unknown
 *                 total must render as unknown, never as a guessed number.
 *   - delivered = Σ delivered_quantity — the pharmacy's own figure; drives
 *                 "what is left to fill" (accepted lags until the ward confirms).
 *   - accepted  = Σ accepted_quantity — the ward's figure; the only thing that
 *                 drives the status band, so progress labels ("12/30") use it.
 *   - delivered > accepted is the durable "delivered, ward hasn't confirmed" signal.
 */
import {
  PRESCRIPTION_ROW_ACTION,
  isActivePrescriptionStatus,
  isCancelledPrescriptionStatus,
  isCompletedPrescriptionStatus,
  isShortClosedPrescriptionStatus,
  normalizePrescriptionStatus,
  prescriptionStatusLabel,
  resolvePrescriptionRowAction,
} from './prescriptionStatus';

/** Non-terminal rows older than this are the historical backlog (pharamcy.md §4.4). */
export const STALE_ORDER_AGE_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

function toNum(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

/** Counters are numeric(12,3); show "12", "12.5" — never "12.000". */
export function formatQty(value) {
  return String(parseFloat(toNum(value).toFixed(3)));
}

/**
 * A line's ordered quantity, or null when it is not knowable. total_quantity is
 * omitted/0 for regimens with no computable total — the backend reads that as
 * "unknown, never a guess" (order_lifecycle.ordered_quantity) and so do we.
 */
function lineDetails(line) {
  return line && typeof line.details === 'object' && line.details !== null ? line.details : {};
}

function lineOrderedQuantity(line) {
  const details = lineDetails(line);
  const total = Number(details.total_quantity ?? line?.total_quantity);
  return Number.isFinite(total) && total > 0 ? total : null;
}

/**
 * Units the ward has confirmed taking on one line.
 *
 * Two places carry this and they disagree. The `accepted_quantity` COLUMN is
 * written only by the gateway's credit_acceptance hook, which is reverted
 * (backend ac3c9e8), so it reads 0.0 on every order ever. `details.
 * accepted_quantity` is written by UpdateIPDPrescriptionStatusView on the ward's
 * own 'Received' post and is the figure that actually exists today.
 *
 * Both measure the same units, so the higher one is the informed answer and a 0
 * from either means only that IT does not know — never their sum.
 */
export function lineAcceptedQuantity(line) {
  return Math.max(toNum(line?.accepted_quantity), toNum(lineDetails(line).accepted_quantity));
}

/**
 * Roll one order's medicines[] up into the numbers every fulfilment surface
 * needs. `ordered`/`remaining` are null (not 0) when ANY line lacks a
 * denominator — a partial sum would understate the order and make "12 of 20"
 * read as nearly done on a 30-unit order.
 */
export function summarizeFulfilment(medicines) {
  const lines = Array.isArray(medicines) ? medicines : [];
  let ordered = 0;
  let delivered = 0;
  let accepted = 0;
  let hasUnknownOrdered = lines.length === 0;
  let linesPending = 0;

  lines.forEach((line) => {
    const lineOrdered = lineOrderedQuantity(line);
    if (lineOrdered == null) hasUnknownOrdered = true;
    else ordered += lineOrdered;
    const lineAccepted = lineAcceptedQuantity(line);
    accepted += lineAccepted;
    // The ward cannot accept what the pharmacy never handed over, so accepted is
    // a floor under delivered. Load-bearing rather than defensive:
    // delivered_quantity is dead (credit_delivery is reverted), so without this
    // an order for 16 that the ward took 8 of reports 0 delivered — and
    // `remaining` then offers all 16 again.
    const lineDelivered = Math.max(toNum(line?.delivered_quantity), lineAccepted);
    delivered += lineDelivered;
    // A line with no knowable total counts as pending: the same "unknown, never
    // a guess" rule the start page applies when it refuses to close such a line.
    if (lineOrdered == null || lineDelivered < lineOrdered) linesPending += 1;
  });

  return {
    ordered: hasUnknownOrdered ? null : ordered,
    delivered,
    accepted,
    remaining: hasUnknownOrdered ? null : Math.max(0, ordered - delivered),
    hasUnknownOrdered,
    // How many MEDICINES are still owed, out of how many the order has.
    //
    // The unit figures above are sums across every line of the order, and across
    // different medicines they do not describe anything: 2 tablets + 2 injections
    // + 2 syrups is not "6" of any real thing. Order-level UI counts medicines
    // instead; units stay per line, where they mean something.
    linesPending,
    linesTotal: lines.length,
  };
}

/**
 * Status-chip label override, or null to use the default label:
 *   Partial                       -> "Partially Dispensed"
 *   ShortClosed                    -> "Closed (Short)"
 *   ShortClosed, nothing accepted  -> "Not Supplied"   (display rule, not a status)
 *   a finished status still owing  -> "Received", "Dispensed" …
 *
 * NO quantity suffix. It used to read "Partially Dispensed 8/16", which is only
 * meaningful on a single-medicine order — on a ten-medicine one those are sums
 * across unrelated UOMs, and a pharmacist reading "10 of 20" thought units were
 * outstanding when in fact five whole medicines had never been touched. The row's
 * action button carries the honest figure instead (fillRemainingLabel), counted
 * in medicines.
 */
export function prescriptionStatusChipLabel(status, fulfilment) {
  const normalized = normalizePrescriptionStatus(status);

  if (normalized === 'SHORT_CLOSED') {
    if (toNum(fulfilment?.accepted) <= 0) return 'Not Supplied';
    return 'Closed (Short)';
  }
  if (normalized === 'PARTIAL') {
    return 'Partially Dispensed';
  }
  // A status that claims the pharmacy is finished, over an order that is not.
  if (hasUnfilledRemainder(status, fulfilment)) {
    return prescriptionStatusLabel(normalized);
  }
  return null;
}

/**
 * The order still owes units, whatever its status claims.
 *
 * The ward's confirm posts `status: 'Received'` for the whole order however
 * little it accepted — 'Partial' is DERIVED from accepted_quantity by
 * credit_acceptance, which is reverted (backend ac3c9e8), so nothing ever sets
 * it. An order for 16 that the ward took 8 of therefore lands on 'Received',
 * which is terminal: it leaves the Open filter, loses its row action, and the
 * other 8 become unreachable. That is the state this predicate rescues.
 *
 * Deliberately NOT a status rewrite — the wire value stays 'Received' wherever
 * it is stored, sent and displayed. Only actionability is re-derived, and only
 * where the denominator is genuinely known. Cancel and ShortClosed are excluded:
 * both mean the remainder is not coming, which is a decision, not a shortfall.
 */
export function hasUnfilledRemainder(status, fulfilment) {
  if (!fulfilment || fulfilment.ordered == null) return false;
  if (isCancelledPrescriptionStatus(status) || isShortClosedPrescriptionStatus(status)) return false;
  if (!isCompletedPrescriptionStatus(status)) return false;
  return toNum(fulfilment.delivered) < toNum(fulfilment.ordered);
}

/**
 * Row action, with the quantities allowed to overrule a status that says done.
 *
 * Delegates to the status-only rule first and only steps in where that returns
 * NONE *and* the order measurably still owes units — so a genuinely complete
 * order is never reopened, and neither is one whose total was never knowable.
 */
export function resolveFulfilmentRowAction(status, fulfilment, canStart) {
  const action = resolvePrescriptionRowAction(status, canStart);
  if (action.mode !== PRESCRIPTION_ROW_ACTION.NONE) return action;
  if (!hasUnfilledRemainder(status, fulfilment)) return action;
  if (!canStart) return { mode: PRESCRIPTION_ROW_ACTION.VIEW_ONLY, label: 'View only' };
  return { mode: PRESCRIPTION_ROW_ACTION.CONTINUE, label: fillRemainingLabel(fulfilment) };
}

/** Can the pharmacy still act on this order — by status, or by what it still owes? */
export function isFillablePrescription(status, fulfilment) {
  return isActivePrescriptionStatus(status) || hasUnfilledRemainder(status, fulfilment);
}

/**
 * Row-action label for an order that still owes work.
 *
 * No numbers. It carried "(18 of 30)" — a sum of units across unrelated UOMs,
 * which read as a quantity remainder when whole medicines were outstanding — and
 * then "(5 of 10 medicines)", which was accurate but still noise on a button the
 * pharmacist is about to click anyway. WHAT is pending, and how much of it,
 * belongs in the prescription dialog where there is room to name each medicine.
 */
export function fillRemainingLabel() {
  return 'Fill remaining';
}

/** Stock has left the pharmacy that the ward has not yet confirmed taking. */
export function hasUnconfirmedDelivery(fulfilment) {
  if (!fulfilment) return false;
  return toNum(fulfilment.delivered) > toNum(fulfilment.accepted);
}

/**
 * Everything ordered has been handed over — nothing left for the pharmacy to
 * fill, only the ward's confirm outstanding. False when the denominator is
 * unknown: with no total we cannot claim "fully", and the start page can still
 * legitimately dispense more.
 */
export function isFullyDelivered(fulfilment) {
  if (!fulfilment || fulfilment.ordered == null) return false;
  return toNum(fulfilment.delivered) >= toNum(fulfilment.ordered);
}

/** Whole days since the order was created, or null when the date is unparseable. */
export function prescriptionAgeDays(createdAt, now = Date.now()) {
  const created = new Date(createdAt || '').getTime();
  if (!Number.isFinite(created)) return null;
  return Math.floor((now - created) / DAY_MS);
}

/**
 * The historical backlog: still-open rows nobody has touched in over a week.
 * NULL-counter detection is impossible here — the serializer coerces NULL to 0
 * (manage_prescription.py) — so age is the only client-side stale signal.
 */
export function isStalePrescription(row, now = Date.now()) {
  if (!isActivePrescriptionStatus(row?.status)) return false;
  const age = prescriptionAgeDays(row?.created_at, now);
  return age != null && age > STALE_ORDER_AGE_DAYS;
}
